import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import moment from 'moment';
import 'mapbox-gl/dist/mapbox-gl.css';
// import mapboxgl from 'mapbox-gl';
import { GeolocateControl, NavigationControl, ScaleControl } from 'react-map-gl/mapbox';
import { useHistory, useLocation } from 'react-router';
import lodash from 'lodash';
import ReactGA from 'react-ga4';
import { Spin } from 'antd';
import MediaQuery from 'react-responsive';
import { useAuthenticator } from '@aws-amplify/ui-react';

import MapControls from './components/MapControls';
import MapFilterPopup from './components/MapFilterPopup';
import ExperienceBranding from './components/ExperienceBranding';
import CustomMap from '../../components/custom-map';
import Landing from '../landing/index';
import appConfig, { brandingConfig } from '../../config/index';

import { MapEvent, MapFilterEvent } from '../../constant/google-analytics/constant';
import {
  platformTags, waterTags, weatherTags, orgTags, overlayTags,
} from './constant';
import {
  datasets,
  getDatasetSummaries,
  getSamplingData,
  getObsLatestData,
  parameterConfigurations,
  datasetTrajectoriesGeojson,
  getRegisteredParameters,
  getDatasetsLatest,
  getExternalObsDatasets,
  getExternalParameters,
  getExternalObsLatestData,
} from '../../services/dataset.service';
import { getOrganizations } from '../../services/organization.service';
import MapQueryTypes from '../../services/query-types/map';
import { getLatestParameterDate, platformGroupings } from '../../utils';
import ProfileQueryTypes from '../../services/query-types/profile';
import { getUserFavoritePlatforms } from '../../services/profile.service';
import { getDefaultExperienceForOrg, getNamedExperienceForOrg } from '../../services/experience.service';

import './styles.scss';
import { useData } from '../../contexts/DataContext';
import UserContext from '../../contexts/UserContext';
import SocialShareRecipientModal from './components/SocialShareRecipientModal';

// This was recommended as a fix for an issue with the basemap from Mapbox not loading
// https://github.com/mapbox/mapbox-gl-js/issues/10173#issuecomment-799627909
// eslint-disable-next-line import/no-webpack-loader-syntax
// mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const scaleControlStyle = {
  bottom: 36,
  left: 0,
  padding: '10px',
};

const Dashboard = (props) => {
  const { match } = props;
  // orgSlug is set on both /map/:orgSlug and /map/:orgSlug/:experienceSlug; experienceSlug only on the latter.
  const orgSlug = match?.params?.orgSlug;
  const experienceSlug = match?.params?.experienceSlug;

  const history = useHistory();
  const location = useLocation();

  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);
  const { setAuthDrawerOpen } = useContext(UserContext);

  const [filterVisible, setFilterVisible] = useState(false);
  const [searchDrawer, toggleSearchDrawer] = useState(true);
  const [editing, setEditing] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState();
  const [selectedOverlay, setSelectedOverlay] = useState('surface_water_temperature');
  const [showPaths, setShowPaths] = useState(true);
  const [omicsFilters, setOmicsFilters] = useState({
    selectedFilter: 'All',
    selectedStartDate: moment('2010-01-01'),
    selectedEndDate: moment(),
  });
  const [showShareModal, setShowShareModal] = useState(false);

  const {
    useGlobalData,
  } = useData();

  const { observationalPlatformsResult } = useGlobalData();

  const handleLandingChange = () => {
    toggleSearchDrawer(false);
    history.replace('/map');
  };

  useEffect(() => {
    if (location.pathname === '/landing') {
      toggleSearchDrawer(true);
    } else {
      toggleSearchDrawer(false);
    }
  }, [location.pathname]);

  // List of all platform tags
  const [tagsList, setTagsList] = useState({
    platforms: [],
    weather: [],
    org: [],
    water: [],
  });

  const defaultPlatformTags = brandingConfig.filters.isNest
    ? platformTags.filter((platform) => platform.key === 'buoy' || platform.key === 'tower' || platform.key === 'nest')
    : platformTags.filter((platform) => platform.key === 'buoy' || platform.key === 'tower');
    // : platformTags.filter((platform) => platform.key === 'buoy' || platform.key === 'tower' || platform.key === 'water_level');

  // Tags displayed on the map
  // Filter by buoys by default
  // const [filterTags, setFilterTags] = useState(platformTags.filter((platform) => platform.key === 'buoy'));
  // *** Make sure what you have for filterTags here matches what's in selectedTags below ***
  const [filterTags, setFilterTags] = useState(
    defaultPlatformTags.concat(
      overlayTags.filter((overlay) => overlay.key === 'surface_water_temperature'),
    ),
  );
  const [selectedLake, setSelectedLake] = useState(brandingConfig.filters.defaultLake);

  const [selectedTags, setSelectedTags] = useState({
    platforms: defaultPlatformTags,
    weather: weatherTags.filter((weather) => weather.key === 'surface_water_temperature'),
    org: [],
    water: [],
  });
  const [platformEvents, setPlatformEvents] = useState(['activated', 'unavailable']);
  const [filterFavorites, setFilterFavorites] = useState(false);
  const [filteredPlatforms, setFilteredPlatforms] = useState(
    platformTags.filter((platform) => platform.key === 'buoy').concat(
      platformTags.filter((platform) => platform.key === 'tower'),
    ),
  );

  const {
    data: organizationsResult,
    isLoading: organizationLoading,
  } = useQuery(MapQueryTypes.REST_ORGANIZATIONS, getOrganizations, { refetchOnWindowFocus: false });

  const {
    data: datasetsResult,
    isLoading: datasetsLoading,
  } = useQuery(MapQueryTypes.REST_OBS_DATASETS, datasets, { refetchOnWindowFocus: false });

  const {
    data: obsDatasetSummariesResult,
    isLoading: summariesLoading,
  } = useQuery(MapQueryTypes.REST_OBS_DATASET_SUMMARIES, () => getDatasetSummaries(), { refetchOnWindowFocus: false });

  const {
    data: externalObsDatasets,
    // isLoading: externalObsDatasetsIsLoading,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_OBS_DATASETS, getExternalObsDatasets, { refetchOnWindowFocus: false });

  const {
    data: externalParameters,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_PARAMETERS, getExternalParameters, { refetchOnWindowFocus: false });

  const {
    data: externalObsLatest,
  } = useQuery(MapQueryTypes.REST_EXTERNAL_OBS_LATEST_DATA, getExternalObsLatestData, { refetchOnWindowFocus: false });

  const {
    data: obsLatest,
    isLoading: obsLatestIsLoading,
  } = useQuery(MapQueryTypes.REST_OBSERVATIONAL_LATEST_DATA, () => getObsLatestData(), { refetchOnWindowFocus: false });

  const {
    data: parameterConfigurationsResult,
  } = useQuery(MapQueryTypes.REST_PARAMETER_CONFIGURATIONS, parameterConfigurations, { refetchOnWindowFocus: false });

  const {
    data: registeredParametersResult,
    isLoading: registeredParametersAreLoading,
  } = useQuery(MapQueryTypes.REST_PARAMETERS_REGISTERED_DASHBOARD, getRegisteredParameters, { refetchOnWindowFocus: false });

  const {
    data: datasetTrajectoriesGeojsonResult,
  } = useQuery(
    MapQueryTypes.REST_OBSERVATIONAL_DATASET_TRAJECTORIES,
    datasetTrajectoriesGeojson,
    {
      refetchOnWindowFocus: false,
      enabled: filterTags?.some((t) => t.key === 'moving'),
    },
  );

  const {
    data: favoritePlatformData,
  } = useQuery(
    ProfileQueryTypes.REST_PROFILE_FAVORITE_PLATFORMS,
    getUserFavoritePlatforms,
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(authStatus === 'authenticated'),
    },
  );

  // Saved Map Experience config -- default for the org if experienceSlug is absent, named one otherwise.
  const {
    data: experienceResult,
    error: experienceError,
  } = useQuery(
    [MapQueryTypes.REST_EXPERIENCE, orgSlug, experienceSlug],
    () => (experienceSlug
      ? getNamedExperienceForOrg(orgSlug, experienceSlug)
      : getDefaultExperienceForOrg(orgSlug)),
    {
      enabled: !!orgSlug,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
  const appliedExperienceConfigRef = useRef(false);
  const [experienceConfigApplied, setExperienceConfigApplied] = useState(!orgSlug);

  // Anonymous access to a group_members_only experience throws a plain Error
  // with no statusCode (see cdnApiClient.js) -- our cue to prompt sign-in.
  useEffect(() => {
    if (experienceError && !experienceError.statusCode) {
      setAuthDrawerOpen(true);
    }
  }, [experienceError]);

  const [omicsPlatformIds, setOmicsPlatformIds] = useState([]);
  const [omicsParameterIds, setOmicsParameterIds] = useState([]);

  // Platform/dataset restriction sourced from a saved Experience's map_configuration.
  const [selectedDatasetIds, setSelectedDatasetIds] = useState([]);
  const [selectedPlatformIds, setSelectedPlatformIds] = useState([]);

  const {
    data: obsDatasetsLatest,
    refetch: obsDatasetsLatestRefetch,
    isInitialLoading: obsDatasetsLatestIsInitialLoading,
  } = useQuery(
    [MapQueryTypes.REST_OBS_DATASETS_LATEST, omicsFilters.startDate, omicsFilters.endDate, omicsPlatformIds, omicsParameterIds],
    () => getDatasetsLatest({
      platformId: omicsPlatformIds,
      parameterId: omicsParameterIds,
      startDate: omicsFilters.startDate.format('YYYY-MM-DD'),
      endDate: omicsFilters.endDate.format('YYYY-MM-DD'),
      includeObservations: true,
    }),
    {
      refetchOnWindowFocus: false,
      enabled: (!!omicsFilters.startDate && !!omicsFilters.endDate && !!omicsPlatformIds?.length && !!omicsParameterIds?.length),
    },
  );

  // Full grab-sample set (small - low thousands of rows), fetched unfiltered so we can
  // work out, per platform_id, which obs_dataset was most recently sampled -- used to
  // dedupe Omics pins below when multiple obs_datasets share one physical site.
  const {
    data: omicsSamplingDataResult,
  } = useQuery(
    [MapQueryTypes.REST_SAMPLING_DATA_ALL, omicsFilters.startDate, omicsFilters.endDate],
    () => getSamplingData({
      startDate: omicsFilters.startDate.format('YYYY-MM-DD'),
      endDate: omicsFilters.endDate.format('YYYY-MM-DD'),
    }),
    {
      refetchOnWindowFocus: false,
      enabled: (!!omicsFilters.startDate && !!omicsFilters.endDate),
    },
  );

  useEffect(() => {
    filterTags.forEach((tag) => {
      if (tag.overlay && tag.key === selectedOverlay) {
        const overlayTags = ['surface_water_temperature', 'water_current', 'wind_current', 'omics', 'wave_height'];
        const nonOverlay = filterTags.filter((o) => !overlayTags.includes(o.key));
        setFilterTags([...nonOverlay, tag]);
      }
    });

    if (selectedOverlay === 'omics') {
      setOmicsFilters({
        selectedFilter: 'All',
        startDate: moment('2010-01-01'),
        endDate: moment(),
      });
    } else {
      setOmicsFilters({
        selectedFilter: 'All',
        startDate: '',
        endDate: '',
      });
    }
  }, [selectedOverlay]);

  const setOmicsQueryParams = () => {
    if (obsDatasetSummariesResult) {
      // TODO when the time comes: generalize to other Omics orgs
      const omicsSummaries = obsDatasetSummariesResult.filter((summary) => summary?.obs_dataset_platform_assignment?.platform?.external_id_type === 'omics_sample_site');
      const uniquePlatformIds = lodash.uniq(omicsSummaries.map((summary) => summary.obs_dataset_platform_assignment?.platform?.platform_id));
      const uniqueParamIds = lodash.uniq(omicsSummaries.flatMap((summary) => summary.obs_dataset_platform_assignment?.platform?.parameters?.map((parameter) => parameter.parameter_id)));
      setOmicsPlatformIds(uniquePlatformIds);
      setOmicsParameterIds(uniqueParamIds);
    }
  };

  const getAndFormatTagsList = (filteredData) => {
    const tagsListData = {};

    tagsListData.platformTags = platformTags.map((tag) => {
      const tagCount = filteredData.filter((feature) => feature.properties.platform_type.includes(tag.key)).length;
      return { ...tag, text: `${tag.text} ${tagCount ? ` (${tagCount})` : ''}`, count: tagCount };
    });

    // only show nest tags in nests
    if (!brandingConfig.filters.isNest) {
      tagsListData.platformTags = tagsListData.platformTags.filter((tag) => tag.type !== 'nest');
    }

    tagsListData.orgTags = orgTags.map((tag) => ({ ...tag, text: `${tag.text}` }));
    tagsListData.weatherTags = weatherTags.map((tag) => ({ ...tag, text: `${tag.text}` }));
    tagsListData.waterTags = waterTags.map((tag) => ({ ...tag, text: `${tag.text}` }));

    setTagsList(tagsListData);
    return tagsListData;
  };

  const filterPlatforms = () => {
    if (observationalPlatformsResult) {
      let filteredData = observationalPlatformsResult;
      if (datasetTrajectoriesGeojsonResult) {
        filteredData.concat(datasetTrajectoriesGeojsonResult.features);
      }

      if (selectedLake) {
        const selectedLakeStr = `lake-${selectedLake.toLowerCase()}`;
        // Normalize in case the API ever returns a display form (e.g. "Lake Erie")
        // instead of the slug form ("lake-erie") this filter expects.
        const toSlug = (value) => (value || '').toLowerCase().replace(/\s+/g, '-');
        filteredData = filteredData.filter((o) => toSlug(o.properties.body_of_water) === selectedLakeStr);
      }

      // For each platform, if platform_event==='activated' but there is no recent data,
      // set platform_event='unavailable'
      if (obsLatest && obsDatasetSummariesResult) {
        for (let i = 0; i < filteredData.length; i++) {
          // Getting platform external_id_type from dataset-summaries,
          // but could in the future potentially be included in the obs-datasets.geojson
          // Since we are re-assigning platform_event later in this loop, eslint considers any access of filteredData[i] unsafe,
          // but should be ok for now.

          // eslint-disable-next-line no-loop-func
          const datasetSummary = obsDatasetSummariesResult.find((o) => o.obs_dataset_id === filteredData[i].properties.obs_dataset_id);
          const platform = datasetSummary?.obs_dataset_platform_assignment?.platform;

          // add the platform to the properties so we can access it later
          filteredData[i].properties.platform = platform;

          const latestDate = getLatestParameterDate(filteredData[i].properties, obsLatest, true);
          const timeSince = moment().diff(moment(latestDate), 'hours');
          filteredData[i].properties.last_updated_parameter_in_hours = timeSince;

          // timeSince will be NaN if latestDate is null, so check for this first.
          // Exclude ESP sites from this event re-assignment. In the future this may be a metadata configuration.
          if (
            (Number.isNaN(timeSince) || timeSince > appConfig.oldDataCutoffInHours)
            && filteredData[i].properties?.platform_event === 'activated'
            && platform?.external_id_type !== 'esp_site'
          ) {
            filteredData[i].properties.platform_event = 'unavailable';
          }
        }
      }

      if (platformEvents && platformEvents.length > 0) {
        filteredData = filteredData.filter((o) => platformEvents.indexOf(o.properties.platform_event) > -1);

        /* Hide Decommissioned platforms by default */
        if (platformEvents.indexOf('decommissioned') === -1) {
          filteredData = filteredData.filter((o) => o.properties.platform_event !== 'decommissioned');
        }
      }

      getAndFormatTagsList(filteredData);

      if (obsDatasetSummariesResult && brandingConfig.filters.isNest) {
        // filter platforms data by selected nest tags
        const nestFilter = filterTags.some((t) => t.key === 'nest');
        const seagullFilter = filterTags.some((t) => t.key === 'seagull');
        const nestDatasetIds = obsDatasetSummariesResult
          .filter((o) => o?.organization_id === brandingConfig.filters.organizationId)
          .map((o) => o.obs_dataset_id);

        // if filtering by nest platforms and seagull platforms have not been added,
        // only show platforms within the nest organization
        if (nestFilter && !seagullFilter) {
          filteredData = filteredData.filter((o) => nestDatasetIds.includes(o.properties.obs_dataset_id));
        }

        // if nest filter is not selected, remove nest platforms
        if (!nestFilter) {
          filteredData = filteredData.filter((o) => !nestDatasetIds.includes(o.properties.obs_dataset_id));
        }
      }

      // filter platforms data by selected platform_type tags
      const oldTags = ['surface_water_temperature', 'water_current', 'wind_current', 'omics', 'sampling_location', 'wave_height'];
      const filterTagsNew = filterTags.filter((o) => !oldTags.includes(o.key));
      if (filterTagsNew) {
        if (filterTagsNew.length > 0) {
          filteredData = filteredData.filter((o) => filterTagsNew.length > 0
            && filterTagsNew.some((tag) => platformGroupings[tag.key]?.includes(o.properties.platform_type)));
        } else {
          filteredData = [];
        }
      } else {
        filteredData = [];
      }

      // Get/Isolate the omics/sampling-location pins directly from obs-dataset-summaries'
      // deployment_sites. Pin existence shouldn't depend on whether "latest observation"
      // data has been synced anywhere for these platforms -- that sync (into
      // latest_observations) is a separate, best-effort process that grab-sample
      // (Omics/GLAMR) data doesn't reliably go through. The dataset/platform metadata
      // here is authoritative on its own.
      if (obsDatasetSummariesResult && selectedOverlay === 'omics') {
        const omicsOrgId = parseInt(process.env.REACT_APP_OMICS_ORG_ID, 10);
        const omicsSummaries = obsDatasetSummariesResult.filter((summary) => (
          summary.organization_id === omicsOrgId
          || summary.obs_dataset_platform_assignment?.platform?.platform_type === 'sampling_location'
        ) && summary.deployment_site?.latitude != null && summary.deployment_site?.longitude != null);

        // Latest sample timestamp per obs_dataset_id, from the full (small) sampling_data
        // set, used below to pick a winner when multiple obs_datasets share a platform_id
        // (e.g. repeat visits to the same physical site).
        const latestTimestampByDataset = {};
        (omicsSamplingDataResult || []).forEach((dataset) => {
          dataset.parameters.forEach((parameter) => {
            parameter.observations.forEach((observation) => {
              const current = latestTimestampByDataset[dataset.obs_dataset_id];
              if (!current || observation.timestamp > current) {
                latestTimestampByDataset[dataset.obs_dataset_id] = observation.timestamp;
              }
            });
          });
        });

        // Prefer, per platform_id, the obs_dataset with the most recent sample. Datasets
        // with no platform_id to dedupe against are kept as-is.
        const bestSummaryByPlatformId = {};
        omicsSummaries.forEach((summary) => {
          const platformId = summary.obs_dataset_platform_assignment?.platform_id;
          const key = platformId ?? `dataset-${summary.obs_dataset_id}`;
          const existing = bestSummaryByPlatformId[key];
          const candidateLatest = latestTimestampByDataset[summary.obs_dataset_id] || '';
          const existingLatest = existing ? (latestTimestampByDataset[existing.obs_dataset_id] || '') : '';
          if (!existing || candidateLatest > existingLatest) {
            bestSummaryByPlatformId[key] = summary;
          }
        });

        const omicsPlatforms = Object.values(bestSummaryByPlatformId).map((summary) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [summary.deployment_site.longitude, summary.deployment_site.latitude],
          },
          properties: {
            obs_dataset_id: summary.obs_dataset_id,
            org_platform_id: summary.org_platform_id,
            platform_name: summary.platform_name,
            platform_type: summary.obs_dataset_platform_assignment?.platform?.platform_type || 'sampling_location',
            platform_event: summary.obs_dataset_platform_assignment?.platform?.platform_event?.event,
            organization_id: summary.organization_id,
          },
        }));
        filteredData = omicsPlatforms.concat(filteredData);
      }

      // Restrict to a saved Experience's platform/dataset selection, if any.
      if (selectedDatasetIds.length) {
        filteredData = filteredData.filter((o) => selectedDatasetIds.includes(o.properties.obs_dataset_id));
      }

      if (selectedPlatformIds.length && obsDatasetSummariesResult) {
        filteredData = filteredData.filter((o) => {
          const summary = obsDatasetSummariesResult.find((s) => s.obs_dataset_id === o.properties.obs_dataset_id);
          return selectedPlatformIds.includes(summary?.obs_dataset_platform_assignment?.platform_id);
        });
      }

      setFilteredPlatforms(filteredData);
    }
  };

  useEffect(() => {
    if (obsDatasetSummariesResult) {
      setOmicsQueryParams();
    }
  }, [obsDatasetSummariesResult]);

  useEffect(() => {
    if (!registeredParametersResult?.length || !parameterConfigurationsResult?.length) {
      return;
    }

    if (omicsFilters.filter === 'All') {
      setOmicsQueryParams();
    } else {
      const parameter = parameterConfigurationsResult?.find((p) => p.display_name.en.includes(omicsFilters.filter));
      if (parameter) {
        const parameterIds = registeredParametersResult
          .filter((p) => p?.standard_name === parameter.standard_name)
          .map((p) => p.parameter_id);

        setOmicsParameterIds(parameterIds);
      } else {
        // eslint-disable-next-line no-console
        console.warn('No parameter found for selected filter');
        setOmicsQueryParams();
      }
    }
  }, [omicsFilters.filter]);

  const urlSearchQuery = new URLSearchParams(location.search);

  if (urlSearchQuery.get('lake') && urlSearchQuery.get('lake') !== selectedLake) {
    setSelectedLake(urlSearchQuery.get('lake'));
  }

  if (urlSearchQuery.get('status') && !lodash.isEqual(urlSearchQuery.get('status').split(','), platformEvents)) {
    setPlatformEvents(urlSearchQuery.get('status').split(','));
  }

  if (urlSearchQuery.get('datasetIds')) {
    const ids = urlSearchQuery.get('datasetIds').split(',').map(Number);
    if (!lodash.isEqual(ids, selectedDatasetIds)) setSelectedDatasetIds(ids);
  }

  if (urlSearchQuery.get('platformIds')) {
    const ids = urlSearchQuery.get('platformIds').split(',').map(Number);
    if (!lodash.isEqual(ids, selectedPlatformIds)) setSelectedPlatformIds(ids);
  }

  if (urlSearchQuery.get('tags')) {
    const tagsQuery = {};
    urlSearchQuery.get('tags').split(',').forEach((o) => {
      const [tag, val] = o.split(':');
      switch (tag) {
        case 'platforms':
          tagsQuery.platforms = platformTags.filter((p) => val.includes(p.key));
          break;
        case 'overlay':
          tagsQuery.overlay = overlayTags.filter((p) => val.includes(p.key));
          break;
        case 'water':
          tagsQuery.water = waterTags.filter((p) => val.includes(p.key));
          break;
        case 'org':
          tagsQuery.org = orgTags.filter((p) => val.includes(p.key));
          break;
        case 'weather':
          tagsQuery.weather = weatherTags.filter((p) => val.includes(p.key));
          break;
        default:
          tagsQuery.favorite = [];
      }
    });

    if (tagsQuery.water?.length) {
      if (tagsQuery.water[0].key !== selectedOverlay) {
        setSelectedOverlay(tagsQuery.water[0].key);
      }
    }

    if (tagsQuery.weather?.length) {
      if (tagsQuery.weather[0].key !== selectedOverlay) {
        setSelectedOverlay(tagsQuery.weather[0].key);
      }
    }

    if (tagsQuery.org?.length) {
      if (tagsQuery.org[0].key !== selectedOverlay) {
        setSelectedOverlay(tagsQuery.org[0].key);
      }
    }

    if (!lodash.isEqual(selectedTags, tagsQuery)) {
      setSelectedTags(tagsQuery);
      setFilterTags(Object.keys(tagsQuery).reduce(
        (prev, cur) => [...prev, ...tagsQuery[cur]],
        [],
      ));
    }
  }

  useEffect(() => {
    if (omicsParameterIds?.length && omicsPlatformIds?.length) {
      obsDatasetsLatestRefetch();
    }
  }, [omicsPlatformIds, omicsParameterIds, obsDatasetSummariesResult]);

  useEffect(() => {
    if (urlSearchQuery.has('editing') && urlSearchQuery.get('editing') === '1') {
      setEditing(true);
    }
  }, [urlSearchQuery]);

  useEffect(() => {
    if (urlSearchQuery.has('favorite_platform') && observationalPlatformsResult) {
      const platform = observationalPlatformsResult.features.find((o) => o.properties.org_platform_id === (urlSearchQuery.get('favorite_platform')));
      setFilterVisible(true);

      if (platform) {
        setSelectedPlatform(platform.properties);
      }
    }
  }, [urlSearchQuery, observationalPlatformsResult]);

  useEffect(() => {
    filterTags.forEach((tag) => {
      if (tag.overlay && tag.key === selectedOverlay) {
        const overlayTags = ['surface_water_temperature', 'water_current', 'wind_current', 'omics', 'wave_height', 'bathy'];
        const nonOverlay = filterTags.filter((o) => !overlayTags.includes(o.key));
        setFilterTags([...nonOverlay, tag]);
      }
    });
  }, [selectedOverlay]);

  useEffect(() => {
    filterPlatforms();
  }, [
    observationalPlatformsResult,
    platformEvents,
    selectedTags,
    filterTags,
    selectedLake,
    datasetTrajectoriesGeojsonResult,
    obsDatasetSummariesResult,
    obsDatasetsLatest,
    obsLatest,
    selectedDatasetIds,
    selectedPlatformIds,
  ]);

  // Buckets selected_layer_keys into the existing 'tags' URL-param shape --
  // reuses current parsing instead of adding a new one.
  const layerKeysToTagsParam = (layerKeys) => {
    const buckets = {
      platforms: layerKeys.filter((key) => platformTags.some((t) => t.key === key)),
      weather: layerKeys.filter((key) => weatherTags.some((t) => t.key === key)),
      water: layerKeys.filter((key) => waterTags.some((t) => t.key === key)),
      org: layerKeys.filter((key) => orgTags.some((t) => t.key === key)),
    };
    return Object.keys(buckets)
      .filter((group) => buckets[group].length)
      .map((group) => `${group}:${buckets[group].join('-')}`)
      .join(',');
  };

  // One-time sync: writes a saved Experience's config into the URL before
  // CustomMap mounts, reusing existing URL hydration. Keeps current pathname.
  if (orgSlug && experienceResult && !appliedExperienceConfigRef.current) {
    appliedExperienceConfigRef.current = true;

    const cfg = experienceResult.map_configuration || {};
    const params = new URLSearchParams(window.location.search);
    if (cfg.coords) params.set('coords', cfg.coords);
    if (cfg.lake) params.set('lake', cfg.lake);
    if (cfg.status) params.set('status', cfg.status);
    if (cfg.tags) {
      params.set('tags', cfg.tags);
    } else if (cfg.selected_layer_keys?.length) {
      params.set('tags', layerKeysToTagsParam(cfg.selected_layer_keys));
    }
    if (cfg.selected_dataset_ids?.length) params.set('datasetIds', cfg.selected_dataset_ids.join(','));
    if (cfg.selected_platform_ids?.length) params.set('platformIds', cfg.selected_platform_ids.join(','));

    const targetPathname = window.location.pathname;
    history.replace({ pathname: targetPathname, search: `?${params.toString()}` });
    setExperienceConfigApplied(true);
  }

  const pushToHistory = (key, value) => {
    let newParams = '';
    // location from useLocation is out of date, so opted to use window.location
    if (window.location.search !== '') {
      const params = new URLSearchParams(window.location.search);

      if (!value) {
        params.delete(key);
      } else {
        params.set(key, value);
      }

      newParams = params.toString();
    } else if (value) {
      const params = new URLSearchParams({ [key]: value });
      newParams = params.toString();
    }

    // Preserve the current pathname instead of always snapping back to plain /map.
    history.replace({
      pathname: window.location.pathname,
      search: `?${newParams}`,
    });
  };

  const onLakeSelect = (lake) => {
    setSelectedLake(lake);
    // don't reset tags if the user is selecting a lake from the map for now
    // setFilterTags([]);
    // setSelectedTags({
    //   platforms: [selectedTags.platforms],
    //   weather: [selectedTags.weather],
    //   org: [selectedTags.org],
    //   water: [],
    // });
    pushToHistory('lake', lake);
    ReactGA.event({ ...MapFilterEvent.LakeFilterSelect, label: `${lake}` });
  };

  const showFilter = () => {
    setFilterVisible(true);
  };

  const onClose = () => {
    setFilterVisible(false);
  };

  const onStatusSelect = (statusList) => {
    const selectedStatuses = statusList.map((stat) => stat.toLowerCase().split(' ').join('_'));
    setPlatformEvents(selectedStatuses);

    pushToHistory('status', selectedStatuses.join(','));
    ReactGA.event({ ...MapFilterEvent.PlatformStatusFilter, label: `${statusList}` });
  };

  const handleClearFilters = () => {
    setSelectedTags({
      platforms: [],
      weather: [],
      org: [],
      water: [],
    });
    setFilterTags([]);
    setSelectedLake('');
    setSelectedOverlay('');
    setPlatformEvents([]);

    history.replace(window.location.pathname);
    ReactGA.event(MapFilterEvent.ClearFilters);
  };

  const handleClearLakeFilters = () => {
    const newHistory = history.location.search.replace(`lake=${selectedLake}`, '');
    history.replace(newHistory);
    setSelectedLake('');
    ReactGA.event(MapFilterEvent.ClearLakeFilters);
  };

  const onTagSelect = (tag, checked, type) => {
    if (tag.overlay) {
      if (tag.forecast && tag.key === selectedOverlay) {
        setSelectedOverlay('');
        setSelectedTags({
          platforms: [selectedTags.platforms],
          weather: [tag.key],
          org: [],
          water: [],
        });
      } else if (tag.key === selectedOverlay) {
        setSelectedOverlay('');
        setSelectedTags({
          platforms: [selectedTags.platforms],
          weather: [],
          org: [tag.key],
          water: [],
        });
      } else {
        setSelectedTags({
          platforms: [selectedTags.platforms],
          weather: [],
          org: [],
          water: [],
        });
        setSelectedOverlay('');
        setSelectedOverlay(tag.key);
      }
    }

    const addTags = type === 'water' || type === 'weather' || type === 'org' ? [tag] : [...selectedTags[type], tag];

    if (!selectedTags) {
      // eslint-disable-next-line no-console
      console.warn('need to hanlde no tags selected');
    }

    const nextSelectedTags = checked
      ? addTags
      : selectedTags[type].filter((t) => t.key !== tag.key);

    const newSelectedTags = type === 'weather'
      ? { ...selectedTags, org: [], [type]: nextSelectedTags }
      : type === 'org'
        ? { ...selectedTags, weather: [], [type]: nextSelectedTags }
        : { ...selectedTags, [type]: nextSelectedTags };

    if (tag.key === 'omics') {
      const samplingLocationTag = tagsList.platformTags.find((t) => t.key === 'sampling_location');
      newSelectedTags.platforms.push(samplingLocationTag);
    }

    const tags = Object.keys(newSelectedTags).reduce(
      (prev, cur) => [...prev, ...newSelectedTags[cur]],
      [],
    );

    setFilterTags(tags);
    setSelectedTags(newSelectedTags);
    pushToHistory('tags', Object.keys(newSelectedTags).map((key) => `${key}:${newSelectedTags[key].map((o) => o.key).join('-')}`).join(','));
    ReactGA.event({ ...MapFilterEvent.TagSelect, label: `${tag.key}` });
  };

  const onTagRemove = (tag) => {
    if (tag.overlay) {
      setSelectedOverlay('');
    }
    const newSelectedTags = { ...selectedTags }; // OK as ref
    const keys = Object.keys(newSelectedTags);
    for (let i = 0; i < keys.length; i++) {
      const tagIdx = newSelectedTags[keys[i]] && newSelectedTags[keys[i]].length > 0
        ? newSelectedTags[keys[i]].map((item) => item.key).indexOf(tag.key)
        : -1;

      if (tagIdx > -1) {
        setSelectedTags({ ...selectedTags }[keys[i]].splice(tagIdx, 1));

        setFilterTags(filterTags.filter((item) => item.key !== tag.key));
        pushToHistory('tags', Object.keys(newSelectedTags).map((key) => `${key}:${newSelectedTags[key].map((o) => o.key).join('-')}`).join(','));
        ReactGA.event({ ...MapFilterEvent.TagRemoved, label: `${tag.key}` });
        break;
      }
    }
  };

  const trackUserDidGeolocate = () => {
    ReactGA.event(MapEvent.UserDidGeolocate);
  };

  // True when a saved Experience restricts platforms/datasets -- used to hide
  // external stations and webcams entirely, since neither can be filtered by ID.
  const hasPlatformDatasetRestriction = !!(selectedDatasetIds.length || selectedPlatformIds.length);

  const isLoading = organizationLoading || summariesLoading || obsLatestIsLoading || datasetsLoading || obsDatasetsLatestIsInitialLoading || registeredParametersAreLoading || (!!orgSlug && !experienceError && !experienceConfigApplied);

  const onClickShare = () => {
    setShowShareModal(true);
  };

  // 401 (anonymous on a group_members_only experience) opens the sign-in drawer above.
  const experienceErrorMessage = experienceError && (
    experienceError.statusCode === 403
      ? "You don't have access to this experience. Contact your group admin to request access."
      : experienceError.statusCode === 404
        ? 'This map experience could not be found.'
        : 'Sign in to view this experience.'
  );

  return (
    experienceError
      ? (
        <div className="custom-loader-wrapper">
          <p style={{ textAlign: 'center', width: '100%' }}>{experienceErrorMessage}</p>
        </div>
      )
      : isLoading
        ? (
          <div className="custom-loader-wrapper">
            <Spin size="large" tip="Loading..." />
          </div>
        )
        : (
          <div className="dashboard-content">
            {orgSlug && experienceResult?.logo_url && (
              <ExperienceBranding logoUrl={experienceResult.logo_url} name={experienceResult.name} />
            )}
            {searchDrawer
              // && false // temporarily hiding the landing page as of 6/20/2025
              && location.pathname !== '/map'
              && !location.pathname?.startsWith('/map/')
              && (
                <Landing landingChange={handleLandingChange} />
              )}
            <MapFilterPopup
              visible={filterVisible}
              editing={editing}
              selectedPlatform={selectedPlatform}
              selectedTags={selectedTags}
              selectedLake={selectedLake}
              platformEvents={platformEvents}
              platformsCount={filteredPlatforms.length}
              tagsList={tagsList}
              onLakeSelect={onLakeSelect}
              onClose={onClose}
              onTagSelect={onTagSelect}
              handleClearFilters={handleClearFilters}
              onStatusSelect={onStatusSelect}
              omicsFilters={omicsFilters}
              setOmicsFilters={setOmicsFilters}
            />
            <SocialShareRecipientModal />
            <CustomMap
              filterVisible={filterVisible}
              selectedLake={selectedLake}
              selectedOverlay={selectedOverlay}
              platformEvents={platformEvents}
              selectedTags={filterTags}
              observationalPlatforms={filteredPlatforms}
              datasets={datasetsResult}
              organizations={organizationsResult}
              obsData={obsLatest}
              datasetSummaries={obsDatasetSummariesResult}
              registeredParameters={registeredParametersResult}
              parameterConfigurations={parameterConfigurationsResult}
              pushToHistory={pushToHistory}
              onTagSelect={onTagSelect}
              tagsList={tagsList}
              filterFavorites={filterFavorites}
              favoritePlatformData={favoritePlatformData}
              datasetTrajectories={datasetTrajectoriesGeojsonResult}
              showPaths={showPaths}
              omicsFilters={omicsFilters}
              setOmicsFilters={setOmicsFilters}
              obsDatasetsLatest={obsDatasetsLatest}
              omicsParameterIds={omicsParameterIds}
              showShareModal={showShareModal}
              setShowShareModal={setShowShareModal}
              externalObsDatasets={hasPlatformDatasetRestriction ? [] : externalObsDatasets}
              externalParameters={externalParameters}
              externalObsLatest={externalObsLatest}
              showWebcams={!hasPlatformDatasetRestriction}
            >
              <MapControls
                tags={filterTags}
                onClick={showFilter}
                onTagRemove={onTagRemove}
                selectedLake={selectedLake}
                onClearLakes={handleClearLakeFilters}
                filterFavorites={filterFavorites}
                setFilterFavorites={setFilterFavorites}
                setShowPaths={setShowPaths}
                onTagSelect={onTagSelect}
                onClickShare={onClickShare}
              />
              <ScaleControl style={scaleControlStyle} />
              <MediaQuery minWidth={767}>
                <NavigationControl
                  className="zoom-controls"
                  showCompass={false}
                  position="bottom-right"
                />
              </MediaQuery>
              <GeolocateControl
                className="geolocate-control"
                positionOptions={{ enableHighAccuracy: true }}
                onClick={trackUserDidGeolocate}
                position="bottom-right"
                style={{ marginBottom: 60 }}
              />
            </CustomMap>
          </div>
        )
  );
};

Dashboard.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      orgSlug: PropTypes.string,
      experienceSlug: PropTypes.string,
    }),
  }),
};

Dashboard.defaultProps = {
  match: null,
};

export default Dashboard;
